import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Contact, Search, Mail, Inbox, User as UserIcon } from 'lucide-react';
import AdminHeader from './AdminHeader';
import { crmContacts, crmContactStats, hideContact, restoreContact, deleteContact } from '../lib/marketingApi';
import { useAdminAuth } from '../context/AdminAuthContext';
import { ViewTabs, ModerationMenu } from './ModerationControls';

const STAGE_STYLE = {
  new: 'bg-slate-100 text-slate-600', subscriber: 'bg-sky-100 text-sky-700',
  lead: 'bg-amber-100 text-amber-700', engaged: 'bg-violet-100 text-violet-700',
  customer: 'bg-emerald-100 text-emerald-700', cold: 'bg-rose-100 text-rose-600',
};

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US', { dateStyle: 'medium' }); } catch (e) { return iso; }
}

export default function ContactsCRM() {
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const isAdmin = user?.role === 'admin';
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [stage, setStage] = useState('');
  const [source, setSource] = useState('');
  const [subscribed, setSubscribed] = useState('');
  const [search, setSearch] = useState('');
  const [view, setView] = useState('active');

  const loadStats = useCallback(() => { crmContactStats().then(setStats).catch(() => {}); }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  const load = useCallback(async () => {
    setItems(null);
    try {
      const d = await crmContacts({ stage: stage || undefined, source: source || undefined, subscribed: subscribed || undefined, search: search || undefined, view, limit: 200 });
      setItems(d.items || []); setTotal(d.total || 0);
    } catch (e) { toast.error('Failed to load contacts'); setItems([]); }
  }, [stage, source, subscribed, search, view]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const reload = () => { load(); loadStats(); };
  const doHide = async (id, reason) => { try { await hideContact(id, reason); toast.success(reason === 'spam' ? 'Marked as spam' : 'Moved to trash'); reload(); } catch (e) { toast.error('Action failed'); } };
  const doRestore = async (id) => { try { await restoreContact(id); toast.success('Restored'); reload(); } catch (e) { toast.error('Restore failed'); } };
  const doPurge = async (id) => { if (!window.confirm('Permanently delete this contact? This cannot be undone.')) return; try { await deleteContact(id); toast.success('Permanently deleted'); reload(); } catch (e) { toast.error('Delete failed'); } };

  const sel = 'h-9 rounded-md border border-slate-300 bg-white px-2.5 text-sm text-slate-700 outline-none focus:border-[#0035A8]';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contacts-crm">
      <AdminHeader active="contacts" />
      <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8">
        <div className="mb-4 flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0035A8]/10 text-[#0035A8]"><Contact className="h-4.5 w-4.5" /></span>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">Contacts</h1>
            <p className="text-sm text-slate-500">One unified record per person — subscribers and enquiries synced together</p>
          </div>
        </div>

        {stats && (
          <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="crm-stat-total">
              <div className="text-xs font-medium uppercase tracking-wide text-slate-400">Total contacts</div>
              <div className="mt-1 text-2xl font-bold text-slate-900">{(stats.total || 0).toLocaleString()}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="crm-stat-subscribers">
              <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-slate-400"><Mail className="h-3.5 w-3.5" /> Subscribed</div>
              <div className="mt-1 text-2xl font-bold text-sky-600">{(stats.subscribers || 0).toLocaleString()}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="crm-stat-leads">
              <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-slate-400"><Inbox className="h-3.5 w-3.5" /> With enquiries</div>
              <div className="mt-1 text-2xl font-bold text-amber-600">{(stats.leads || 0).toLocaleString()}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="text-xs font-medium uppercase tracking-wide text-slate-400">Top sources</div>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {(stats.by_source || []).slice(0, 4).map((s) => (
                  <span key={s.source} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">{s.source} · {s.count}</span>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <ViewTabs view={view} setView={setView} counts={{ spam: stats?.hidden_spam, deleted: stats?.hidden_deleted }} testidPrefix="contacts" />
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / email / company" data-testid="crm-search"
              className="h-9 w-64 rounded-md border border-slate-300 bg-white pl-8 pr-3 text-sm outline-none focus:border-[#0035A8]" />
          </div>
          <select value={stage} onChange={(e) => setStage(e.target.value)} className={sel} data-testid="crm-filter-stage">
            <option value="">All stages</option>
            {(stats?.stages || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={subscribed} onChange={(e) => setSubscribed(e.target.value)} className={sel} data-testid="crm-filter-subscribed">
            <option value="">All</option><option value="true">Subscribed</option><option value="false">Not subscribed</option>
          </select>
          <span className="ml-auto text-sm text-slate-500">{total.toLocaleString()} contacts</span>
        </div>

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="hidden grid-cols-12 gap-3 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 md:grid">
            <div className="col-span-3">Name / Email</div><div className="col-span-2">Company</div>
            <div className="col-span-2">Assigned to</div><div className="col-span-1">Stage</div>
            <div className="col-span-1">Sub.</div><div className="col-span-3 text-right">Last contacted</div>
          </div>
          {items === null ? (
            <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-slate-400"><Contact className="h-8 w-8" /><p className="text-sm">No contacts match these filters.</p></div>
          ) : items.map((c) => (
            <div key={c.id} role="button" tabIndex={0} onClick={() => navigate(`/admin/contacts/${c.id}`)} onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/admin/contacts/${c.id}`); }} data-testid={`crm-row-${c.email}`}
              className="grid w-full cursor-pointer grid-cols-1 gap-2 border-b border-slate-100 px-5 py-3 text-left transition-colors hover:bg-slate-50 md:grid-cols-12 md:items-center md:gap-3">
              <div className="col-span-3 min-w-0">
                <div className="truncate text-sm font-semibold text-slate-900">{c.name || c.email}</div>
                <div className="truncate text-xs text-slate-400">{c.email}</div>
              </div>
              <div className="col-span-2 truncate text-sm text-slate-600">{c.company || '—'}</div>
              <div className="col-span-2 min-w-0 truncate text-sm">
                {c.owner ? <span className="inline-flex items-center gap-1 text-slate-700"><UserIcon className="h-3 w-3 text-slate-400" />{c.owner}</span> : <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">Unassigned</span>}
              </div>
              <div className="col-span-1"><span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STAGE_STYLE[c.stage] || 'bg-slate-100 text-slate-600'}`}>{c.stage || 'new'}</span></div>
              <div className="col-span-1">
                {c.subscription?.status === 'subscribed'
                  ? <span className="inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">Yes</span>
                  : <span className="text-xs text-slate-400">—</span>}
              </div>
              <div className="col-span-3 flex items-center justify-end gap-1 text-xs text-slate-500">
                <span className="hidden sm:inline">{c.last_contacted_at ? fmt(c.last_contacted_at) : <span className="text-slate-300">Not contacted</span>}</span>
                <ModerationMenu view={view} isAdmin={isAdmin} testid={`contact-${c.id}`} onSpam={() => doHide(c.id, 'spam')} onDelete={() => doHide(c.id, 'deleted')} onRestore={() => doRestore(c.id)} onPurge={() => doPurge(c.id)} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
